import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Loader2, Minus, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({ annual_price_ht: 189, tva_rate: 0.20, currency: "EUR" });
  const [userType, setUserType] = useState(user?.user_type || "particulier");
  const [quantity, setQuantity] = useState(1);

  const contractType = userType === "professionnel" ? "PRO" : "PARTICULIER";
  const priceHT = config.annual_price_ht;
  const totalHT = priceHT * quantity;
  const totalTVA = Math.round(totalHT * config.tva_rate * 100) / 100;
  const totalTTC = Math.round((totalHT + totalTVA) * 100) / 100;

  useEffect(() => {
    // Fetch config
    const fetchConfig = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/config`);
        setConfig(data);
      } catch (error) {
        console.error("Error fetching config:", error);
      }
    };
    fetchConfig();

    // Check stored user type
    const storedType = sessionStorage.getItem("selectedUserType");
    if (storedType) {
      setUserType(storedType);
    }
  }, []);

  const handleQuantityChange = (delta) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };

  const totalAmount = totalTTC;

  const handleCheckout = async () => {
    setLoading(true);
    
    try {
      // Create order with quantity
      const { data: order } = await axios.post(
        `${API_URL}/api/orders/create`,
        { user_type: userType, quantity, contract_type: contractType },
        { withCredentials: true }
      );

      // Create checkout session
      const { data: checkout } = await axios.post(
        `${API_URL}/api/payments/checkout/create`,
        {
          order_id: order.id,
          origin_url: window.location.origin },
        { withCredentials: true }
      );

      // Redirect to Stripe
      if (checkout.url) {
        window.location.href = checkout.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création du paiement");
      setLoading(false);
    }
  };

  const features = [
    "Jusqu'à 50 000€ de couverture vandalisme",
    "Punaises et parasites couverts jusqu'à 10 000€",
    "Remboursement sous 24 à 48h",
    "Perte d'exploitation incluse jusqu'à 2 semaines de loyers",
    "Frais de relogement jusqu'à 1 000€",
    "Objets de loisirs couverts jusqu'à 1 000€",
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-['DM_Sans']">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#19294C] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au tableau de bord
          </Link>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden" data-testid="checkout-card">
            {/* Header */}
            <div className="bg-[#19294C] px-6 sm:px-8 py-6 text-white">
              <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit']">
                Souscrire à HostProtect
              </h1>
              <p className="mt-2 text-blue-200">
                Protection complète pour vos logements en location courte durée
              </p>
            </div>

            {/* Content */}
            <div className="p-6 sm:p-8">
              {/* Quantity Selector */}
              <div className="mb-8 pb-8 border-b border-slate-100">
                <h2 className="text-lg font-semibold text-[#19294C] font-['Outfit'] mb-4">
                  Nombre de logements à protéger
                </h2>
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                    data-testid="decrease-quantity-btn"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="text-3xl font-bold text-[#19294C] w-16 text-center" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(1)}
                    data-testid="increase-quantity-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  {quantity} logement{quantity > 1 ? 's' : ''} × {priceHT}€ HT/an
                </p>
              </div>

              {/* Pricing */}
              <div className="mb-8 pb-8 border-b border-slate-100">
                <p className="text-sm text-slate-500 uppercase tracking-wide text-center">Récapitulatif</p>
                <div className="mt-4 space-y-2.5" data-testid="pricing-breakdown">
                  <div className="flex items-baseline justify-between text-slate-600">
                    <span>Total HT</span>
                    <span className="font-medium text-slate-900">{totalHT.toFixed(2)} €</span>
                  </div>
                  <div className="flex items-baseline justify-between text-slate-600">
                    <span>TVA (20%)</span>
                    <span className="font-medium text-slate-900">{totalTVA.toFixed(2)} €</span>
                  </div>
                  <div className="pt-3 mt-2 border-t border-slate-200 flex items-baseline justify-between">
                    <span className="font-semibold text-[#19294C]">Total TTC</span>
                    <span
                      className="text-3xl sm:text-4xl font-bold text-[#19294C] font-['Outfit']"
                      data-testid="total-amount"
                    >
                      {totalTTC.toFixed(2)} €
                    </span>
                  </div>
                </div>
                <p className="mt-3 text-center text-sm text-slate-400">
                  Soit {(totalTTC / 365).toFixed(2)}€ TTC/jour pour {quantity} logement{quantity > 1 ? 's' : ''}
                </p>
              </div>

              {/* Features */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-[#19294C] font-['Outfit'] mb-4">
                  Ce qui est inclus pour chaque logement :
                </h3>
                <ul className="space-y-3">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[#00B8E4]" />
                      </div>
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* User Type Display */}
              <div className="mb-8 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Type de compte :</p>
                <p className="font-medium text-slate-900 capitalize">{userType}</p>
              </div>

              {/* CTA */}
              <Button
                onClick={handleCheckout}
                disabled={loading}
                className="w-full bg-[#00B8E4] hover:bg-[#00a5ce] text-white py-4 text-lg font-semibold shadow-md"
                data-testid="checkout-pay-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Redirection vers le paiement...
                  </>
                ) : (
                  <>Payer {totalTTC.toFixed(2)}€ TTC</>
                )}
              </Button>

              {/* Footer info */}
              <div className="mt-6 text-center text-sm text-slate-500 space-y-1">
                <p>Paiement sécurisé par Stripe</p>
                <p>Franchise de 10% du sinistre (minimum 150€) par sinistre déclaré</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckoutPage;
